import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import { Trash, SignOut, Users } from '@phosphor-icons/react';
import type { DeleteAccountResponse } from 'api/account/handlers/deleteAccountHandler';
import type { LeaveAccountResponse } from 'api/account/handlers/leaveAccountHandler';
import type { DeleteAccountRequest } from 'common/account/DeleteAccountDocuments';
import type { LeaveAccountRequest } from 'common/account/LeaveAccountDocuments';
import { AccountUserRole } from 'common/CommonConstants';
import Api from 'client/lib/Api';
import { useError } from 'client/lib/Error';
import { useUser } from 'client/lib/User';
import { useAccountState } from 'client/state/AccountState';
import { Button } from 'client/components/Button';
import { useConfirmDelete } from 'client/components/Dialog/DeleteDialog';

const containerStyle = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
`;

export const AccountActions: FC = () => {
  const navigate = useNavigate();
  const { setError } = useError();
  const confirmDelete = useConfirmDelete();
  const { updateSession } = useUser();

  const accountID = useAccountState((state) => state.document?.id || null);
  const name = useAccountState((state) => state.document?.name || '');
  const isAdmin = useAccountState((state) => state.document?.accountUserRoleID === AccountUserRole.ADMINISTRATOR);

  const handleLeave = async () => {
    if (!accountID) return;
    const isOk = await confirmDelete({
      targetName: name || '',
      targetType: 'account',
      action: 'leave',
      icon: <Users />,
      buttonIcon: <SignOut />,
      isPermanent: false,
    });
    if (!isOk) return;
    const response = await Api.post<LeaveAccountRequest, LeaveAccountResponse>('/account/leave', {
      id: accountID,
    });
    if (response.status === 'last admin cannot leave') {
      setError('Account has to have at least one admin user. Please promote someone before leaving.', {
        errorKey: 'delete-account',
        status: response.status,
      });
      return;
    }
    if (response.status !== 'ok') {
      setError('Unable to leave account. Please try again…', { errorKey: 'delete-account', status: response.status });
      return;
    }
    await updateSession();
    navigate(`/settings`);
  };

  const handleDelete = async () => {
    if (!accountID) return;
    const isOk = await confirmDelete({ targetName: name || '', targetType: 'account' });
    if (!isOk) return;
    const response = await Api.post<DeleteAccountRequest, DeleteAccountResponse>('/account/delete', {
      id: accountID,
    });
    if (response.status === 'account has cores') {
      setError('All cores must be removed from an account before it can be deleted.', {
        errorKey: 'delete-account',
        status: response.status,
      });
      return;
    }
    if (response.status !== 'ok') {
      setError('Unable to delete account. Please try again…', { errorKey: 'delete-account', status: response.status });
      return;
    }
    await updateSession();
    navigate(`/settings`);
  };

  return (
    <div css={containerStyle}>
      <Button danger icon={<SignOut />} onClick={handleLeave}>
        Leave Account
      </Button>
      {isAdmin && (
        <Button danger icon={<Trash />} onClick={handleDelete}>
          Delete Account
        </Button>
      )}
    </div>
  );
};
